import React, {
  FC,
  useRef,
  useState,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
  RefObject,
  useLayoutEffect,
} from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Input,
  Pressable,
  Button,
  Image,
  Switch,
  Spinner,
  ScrollView,
} from "native-base";
import { Animated } from "react-native";
import * as AV from "../../AVCore/AVCore";
import { SignalRService } from "../../AVCore/SignalR";
import useLiveSignalR from "../../Hooks/useLiveSignalR";
import Tooltip from "@mui/material/Tooltip";
import {
  AddIcon,
  LiveIcon,
  LiveMinIcon,
  LiveMaxIcon,
  DropdownIcon,
  LiveRecordIcon,
  PaperPlaneIcon,
  DollarCoinsIcon,
  PreLivestreamIcon,
  LiveRecordStopIcon,
  LiveRecordStartIcon,
} from "../../assets/icons/icon-assets";
import _ from "lodash";
import inform from "../../assets/images/inform.png";
import {
  FiMic,
  FiMicOff,
  FiVideo,
  FiVideoOff,
  FiUsers,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import AVCancel from "./AVCancel";
import { useWindowDimension } from "../../Utility/WindowDimensions";
import { Loc } from "../../App/Loc";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setEntitydata,
  setPostPopupExpanded,
} from "../../Redux/Actions/userActions";
import { setFeeds } from "../../Redux/Actions/feedsAction";
import { setHostLiveMinimized } from "../../Redux/Actions/liveStreamActions";
import settings from "../../Platform/settings.json";
import CustomToast from "../../Hooks/useCustomToast";
import EntityProfilePic from "../../components/common/EntityProfilePic";
import PurchaseCreditsPopup from "../../components/common/PurchaseCreditsPopup";
import AVConfirmationModal from "../../components/common/AVConfirmationModal";
import AgoraRTC from "agora-rtc-sdk-ng";

/* -------------------- AGORA CONFIG -------------------- */
const AGORA_APP_ID = "4ec5b1ce4f0a40f08c7fb81c6ec10e60";
const HOST_COST_PER_MIN = 50; // credits
const HOST_START_CREDITS = 250; // 5 min

interface AVLiveHostPopupProps {
  host: AV.IAVEntity;
  owner: AV.IAVEntity;
  setEntity?: Dispatch<SetStateAction<AV.IAVEntity>>;
  showLiveHost?: AV.ILiveStream | null;
  setShowLiveViewer?: Dispatch<SetStateAction<AV.ILiveStream | null>>;
  isHuddlePostTab: boolean;
  onClose: () => void;
}

type VideoUser = {
  uid: string;
  videoTrack: any;
  isLocal?: boolean;
  networkQuality?: number;
};

/* -------------------- OUTSIDE CLICK HOOK -------------------- */
function useOutsideAlerter(ref: RefObject<any>, callback: () => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}

const AVLiveHostPopup: FC<AVLiveHostPopupProps> = ({
  host,
  owner,
  showLiveHost,
  setShowLiveViewer,
  setEntity,
  isHuddlePostTab,
  onClose,
}) => {
  const [open, setOpen] = useState(true);

  const dimensions = useWindowDimension();
  const isMobile = dimensions?.width < 600;
  const dispatch = useDispatch();

  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  );
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );
  const isMinimized = useSelector(
    (state: RootStateOrAny) => state.liveStream.isHostLiveMinimized,
  );
  const userfeeds = useSelector(
    (state: RootStateOrAny) => state.feeds.feeds[0],
  );

  // LIVE STATE
  const [isLive, setIsLive] = useState(false);

  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState(false);
  const [viewOption, setViewOption] = useState(
    owner?.type === "Person"
      ? Loc.currentLang.menu.followers
      : Loc.currentLang.menu.private,
  );
  const visibilityMap: Record<string, string> = {
    [Loc.currentLang.menu.private]: "Private",
    [Loc.currentLang.menu.followers]: "Follow",
  };

  const [isAPICall, setIsAPICall] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmType, setConfirmType] = useState("");
  const [entity, setEntityData] = useState<AV.IAVEntity>(owner);
  const [sharedPlan, setSharedPlan] = useState<AV.ISharedPlan>();
  const [showCreditsPopup, setShowCreditsPopup] = useState(false);
  const [creditsAvailable, setCreditsAvailable] = useState<number>(0);
  const [showLowCreditPopup, setShowLowCreditPopup] = useState(false);
  const [showMoreCreditPopup, setShowMoreCreditPopup] = useState(false);
  const [eventParentEntity, setEventParentEntity] = useState<AV.IAVEntity>();
  const [finalEntity, setFinalEntity] = useState<AV.IAVEntity | undefined>(
    owner,
  );
  const [liveStream, setLiveStream] = useState<AV.ILiveStream | null>(
    showLiveHost ?? null,
  );
  const [showOnlyVideo, setShowOnlyVideo] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingLoading, setIsRecordingLoading] = useState(false);
  const recordingStartTimeRef = useRef<number>(0);
  const [recordingDuration, setRecordingDuration] = useState("00:00:00");
  const recordingStartedAfterLiveRef = useRef(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef(0);

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollPositionRef.current;
    }
  });

  /* ===================== POPUP STYLES ===================== */
  const popupOverlay = {
    position: "fixed" as const,
    bg: "rgba(0,0,0,0.5)",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 999999,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    display: "flex" as const,
  };

  const popupBox = {
    bg: "white",
    p: 6,
    borderRadius: "12px",
    width: "340px",
    shadow: "9",
  };

  /* ===================== TEXT FORMAT HELPER ===================== */
  // Replace placeholders like {name} in strings
  const formatText = (text: string, values: any[]) => {
    let index = 0;

    return text
      .replace(/\{\s*(.*?)\s*\}/g, () => {
        return values[index++] ?? "";
      })
      .replace(/\s+([.,!?])/g, "$1")
      .replace(/\b(\w+)\s+s\b/g, "$1s");
  };

  //#region Livestream Section

  /* ===================== VIDEO USERS ===================== */
  const [videoUsers, setVideoUsers] = useState<VideoUser[]>([]);

  // Store local audio/video tracks
  const localTracksRef = useRef<any[]>([]);

  /* ===================== MIC CONTROL ===================== */
  const [isMicMuted, setIsMicMuted] = useState(false);

  const toggleMic = async () => {
    const audioTrack = localTracksRef.current[0];
    if (!audioTrack) return;

    if (isMicMuted) {
      await audioTrack.setEnabled(true); // Unmute
      setIsMicMuted(false);
    } else {
      await audioTrack.setEnabled(false); // Mute
      setIsMicMuted(true);
    }
  };

  /* ===================== VIDEO CONTROL ===================== */
  const [isVideoMuted, setIsVideoMuted] = useState(false);

  const toggleVideo = async () => {
    const videoTrack = localTracksRef.current[1];
    if (!videoTrack) return;

    if (isVideoMuted) {
      await videoTrack.setEnabled(true); // Enable video
      setIsVideoMuted(false);
    } else {
      await videoTrack.setEnabled(false); // Disable video
      setIsVideoMuted(true);
    }
  };

  /* ===================== ADD USER ===================== */
  const addUser = (uid: string | number, videoTrack: any, isLocal = false) => {
    const id = String(uid);

    setJoinUsers((prev: AV.ILink[]) => {
      const exists = prev.some((item) => item?.id === currentUser?.id);
      return exists ? prev : [...prev, AV.AVEntity.getLink(currentUser)];
    });

    setVideoUsers((prev) => {
      const exists = prev.find((u) => u.uid === id);
      if (exists) return prev;

      return [...prev, { uid: id, videoTrack, isLocal }];
    });
  };

  /* ===================== REMOVE USER ===================== */
  const removeUser = (uid: string | number) => {
    const id = String(uid);
    setVideoUsers((prev) => prev.filter((u) => u.uid !== id));
  };

  /* ===================== GRID CALCULATION ===================== */
  const getColumns = (count: number) => {
    if (count <= 1) return 1;
    if (count === 2) return 2;
    if (count <= 4) return 2;
    if (count <= 6) return 3;
    if (count <= 9) return 3;
    return 4;
  };

  // Calculate layout
  const columns = getColumns(videoUsers.length);
  const itemWidth = `${100 / columns}%`;

  /* ===================== NETWORK LABEL ===================== */
  const getNetworkLabel = (q?: number) => {
    switch (q) {
      case 1:
        return "Excellent";
      case 2:
        return "Good";
      case 3:
        return "Poor";
      case 4:
        return "Bad";
      case 5:
        return "Very Bad";
      case 6:
        return "Disconnected";
      default:
        return "";
    }
  };

  /* ===================== NETWORK COLOR ===================== */
  const getColor = (q?: number) => {
    if (!q) return "gray.400";

    if (q <= 2) return "green.400";
    if (q <= 4) return "yellow.400";
    return "red.400";
  };

  /* ===================== REFS ===================== */
  const clientRef = useRef<any>(null); // Agora client instance
  //const tracksRef = useRef<any[]>([]);
  const localVideoRef = useRef<HTMLDivElement | null>(null); // Local video DOM container
  const dropdownRef = useRef(null); // Menu reference (for click outside)
  useOutsideAlerter(dropdownRef, () => setOpenDropdown(false)); // Close menu when clicking outside

  /* ===================== INIT CAMERA PREVIEW ===================== */
  const initPreview = async () => {
    // Prevent creating multiple camera/mic tracks
    if (localTracksRef.current.length) return;

    // Create mic + camera tracks using Agora
    const tracks = await AgoraRTC.createMicrophoneAndCameraTracks();

    // Clean up any existing tracks (safety)
    localTracksRef.current.forEach((t) => {
      t.stop();
      t.close();
    });

    // Store new tracks
    localTracksRef.current = tracks;

    // Add preview video (local user before going live)
    addUser("local-preview", tracks[1], true);
  };

  /* ===================== START LIVE STREAM ===================== */
  const startLive = async () => {
    try {
      /* ---------- CHECK CREDITS ---------- */
      if (creditsAvailable < HOST_START_CREDITS) {
        setShowMoreCreditPopup(true);
        return false;
      }

      setIsAPICall(true);

      /* ---------- CREATE AGORA CLIENT ---------- */
      const client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
      clientRef.current = client;

      // Set role as host
      await client.setClientRole("host");

      /* ---------------- AGORA EVENTS ---------------- */

      // When another user publishes video/audio
      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);

        if (mediaType === "video") {
          addUser(user.uid, user.videoTrack);
        }

        if (mediaType === "audio") {
          user.audioTrack?.play();
        }
      });

      // Network quality indicator
      client.on("network-quality", (stats) => {
        setVideoUsers((prev) =>
          prev.map((user) => {
            if (user.isLocal) {
              return {
                ...user,
                networkQuality: stats.uplinkNetworkQuality,
              };
            }

            return {
              ...user,
              networkQuality: stats.downlinkNetworkQuality,
            };
          }),
        );
      });

      // Remove user when unpublished / left
      client.on("user-unpublished", (user) => {
        removeUser(user.uid);
      });

      client.on("user-left", (user) => {
        removeUser(user.uid);
      });

      /* ---------------- CREATE LIVE SESSION ---------------- */

      let liveChannelId = showLiveHost?.channelId ?? "";
      let liveToken = showLiveHost?.token ?? "";

      // If not already live → create new session
      if (!showLiveHost) {
        const params = {
          host: AV.AVEntity.getFk(host),
          owner: AV.AVEntity.getFk(entity),
          visibility:
            entity?.type === "Event" ||
            //entity?.type === "Game" ||
            entity?.type === "Channel"
              ? "Private"
              : visibilityMap[viewOption],
          recording: isRecording,
          webRecording: !isMobile,
        };

        const live = await AV.Media.startLive(params);

        const { channelId, token } = live;

        liveChannelId = channelId;
        liveToken = token;

        sessionIdRef.current = channelId;
        setLiveStream(live);
        setIsLive(true);

        if (isRecording) {
          setIsRecording(true);
          recordingStartedAfterLiveRef.current = false;
          recordingStartTimeRef.current = new Date(live.date!).getTime();
          setRecordingDuration("00:00:00");
        }
      }

      /*  JOIN SIGNALR LIVE CHAT */
      await SignalRService.joinLiveChat(liveChannelId);

      /* ---------------- JOIN AGORA CHANNEL ---------------- */

      await client.join(AGORA_APP_ID, liveChannelId, liveToken, null);
      setIsLive(true);
      const tracks = localTracksRef.current;

      // Remove preview user
      removeUser("local-preview");

      // Add actual live video
      addUser("local-live", tracks[1], true);

      // Play video locally
      if (localVideoRef.current) {
        tracks[1].play(localVideoRef.current, {
          fit: "contain",
        });
      }

      // Publish audio/video
      await client.publish(tracks);

      /* START CREDIT TIMER */
      startChargeTimer();

      setIsAPICall(false);
      console.log("LIVE STARTED");
    } catch (error) {
      //setIsLive(false);
      setIsAPICall(false);
      console.error("Start live error", error);
    }
  };

  /* ===================== LEAVE LIVE ===================== */
  const leaveLive = async () => {
    try {
      setIsAPICall(true);
      stopChargeTimer();

      /* ---------- SEND LIVE END FIRST ---------- */
      if (sessionIdRef.current) {
        await SignalRService.sendLiveEnd(sessionIdRef.current);
      }

      /* ---------- CLOSE SESSION IN BACKEND ---------- */
      if (liveStream) {
        const ml = await AV.Media.closeLiveSession({
          target: AV.AVEntity.getFk(entity),
          minutes: accumulatedMinutes.current,
          mode: "Host",
          channelId: liveStream?.channelId ?? "",
          resourceId: liveStream?.resourceId ?? "",
          sid: liveStream?.sid ?? "",
          recording: isRecording,
          visibility:
            entity?.type === "Event" ||
            //entity?.type === "Game" ||
            entity?.type === "Channel"
              ? "Private"
              : visibilityMap[viewOption],
        });

        console.log("ml", ml);

        if (entity?.id === ml?.id) {
          setEntity?.((prev) =>
            prev
              ? {
                  ...prev,
                  albums: ml.albums,
                }
              : prev,
          );

          setEntityData?.((prev) =>
            prev
              ? {
                  ...prev,
                  albums: ml.albums,
                }
              : prev,
          );

          const updatedList = entityDataList.map((item) =>
            item.id === ml.id
              ? {
                  ...item,
                  albums: ml.albums,
                }
              : item,
          );

          dispatch(setEntitydata(updatedList));
        }

        await updateFeeds();
      }

      accumulatedMinutes.current = 0;

      /* ---------- REMOVE PREVIEW ---------- */
      removeUser("local-preview");

      /* ---------- STOP ALL TRACKS ---------- */
      if (localTracksRef.current?.length) {
        localTracksRef.current.forEach((track) => {
          try {
            track.stop();
            track.close();

            // Force stop camera (important fix)
            const mediaTrack =
              track.getMediaStreamTrack?.() || track._mediaStreamTrack;

            if (mediaTrack) {
              mediaTrack.stop();
            }
          } catch (err) {
            console.warn("Track cleanup error", err);
          }
        });
      }

      localTracksRef.current = [];

      /* ---------- DESTROY CLIENT ---------- */
      if (clientRef.current) {
        await clientRef.current.leave();
        clientRef.current.removeAllListeners();
        clientRef.current = null;
      }

      /* LEAVE SIGNALR GROUP */
      if (sessionIdRef.current) {
        await SignalRService.leaveLiveChat(sessionIdRef.current);
      }

      /* ---------- RESET UI ---------- */
      setVideoUsers([]);
      setLiveStream(null);

      onClose();
      dispatch(setPostPopupExpanded(false));
      dispatch(setHostLiveMinimized(false));
      setConfirmType("");
      setConfirmText("");
      setIsConfirm(false);
      setIsAPICall(false);
    } catch (e) {
      setConfirmType("");
      setConfirmText("");
      setIsConfirm(false);
      setIsAPICall(false);
      console.warn("Leave live error", e);
    }
  };

  const handleLeaveLiveConfirmation = () => {
    setIsConfirm(true);
    setConfirmType("LeaveLive");
    setConfirmText(Loc.currentLang.liveStream.confirmHostLive);
  };

  /* ===================== CREDIT TIMER ===================== */
  const chargeTimer = useRef<any>(null);
  const accumulatedMinutes = useRef<number>(0);
  const sessionIdRef = useRef<string | null>(null);
  const entityRef = useRef(entity);
  const entityDataListRef = useRef(entityDataList);

  /* ---------- KEEP LATEST STATE ---------- */
  useEffect(() => {
    entityRef.current = entity;
  }, [entity]);

  useEffect(() => {
    entityDataListRef.current = entityDataList;
  }, [entityDataList]);

  /* ---------- SAFE PLAN BUILDER ---------- */
  const safePlan = (plan: any, credits: number) => ({
    storeLimit: plan?.storeLimit ?? 0,
    baseStoreLimit: plan?.baseStoreLimit ?? 0,
    payStoreEndDate: plan?.payStoreEndDate ?? "",
    topEntities: plan?.topEntities ?? 0,
    storeUsed: plan?.storeUsed ?? 0,
    shared: plan?.shared,
    credits,
  });

  /* ---------- UPDATE ENTITY LIST ---------- */
  const updateEntityCredits = (
    list: any[],
    entityId: string,
    newCredits: number,
  ) => {
    if (!entityId) return list;

    return list.map((item) => {
      if (item?.id === entityId) {
        return {
          ...item,
          plan: safePlan(item.plan, newCredits),
        };
      }
      return item;
    });
  };

  /* ---------- START TIMER ---------- */
  const startChargeTimer = () => {
    if (chargeTimer.current) return;

    chargeTimer.current = setInterval(async () => {
      try {
        accumulatedMinutes.current += 1;

        const currentEntity =
          entityRef?.current?.type !== "Event" &&
          entityRef?.current?.type !== "Game"
            ? entityRef?.current
            : eventParentEntity;
        const currentList = entityDataListRef.current;
        if (!currentEntity) return;

        const oldCredits = currentEntity?.plan?.credits ?? 0;

        const credits = await AV.Media.chargeLiveCredits({
          target: AV.AVEntity.getFk(currentEntity),
          minutes: 1,
          mode: "Host",
          channelId: "",
          resourceId: "",
          sid: "",
          recording: isRecording,
        });

        console.log("OLD:", oldCredits, "NEW:", credits);

        /* -------------------- UI -------------------- */
        setCreditsAvailable(credits);

        let updatedList = [...currentList];

        /* -------------------- OWN OR SHARED -------------------- */
        const ownUsed = credits !== oldCredits;

        if (currentEntity?.type !== "Event" && currentEntity?.type !== "Game") {
          if (ownUsed) {
            updatedList = updateEntityCredits(
              updatedList,
              currentEntity?.id ?? "",
              credits,
            );

            setEntity?.((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );

            setEntityData?.((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          } else if (currentEntity?.plan?.shared?.id) {
            updatedList = updateEntityCredits(
              updatedList,
              currentEntity.plan.shared.id,
              credits,
            );

            setSharedPlan((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          }

          setFinalEntity((prev) =>
            prev
              ? {
                  ...prev,
                  plan: safePlan(prev.plan, credits),
                }
              : prev,
          );
        } else {
          /* ================= EVENT/GAME ================= */
          const parent = currentList.find(
            (x) => x?.id === currentEntity?.parent?.id,
          );

          if (!parent) return;

          if (ownUsed) {
            updatedList = updateEntityCredits(updatedList, parent.id, credits);

            setEventParentEntity((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          } else if (parent?.plan?.shared?.id) {
            updatedList = updateEntityCredits(
              updatedList,
              parent.plan.shared.id,
              credits,
            );

            setSharedPlan((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          }

          setEntity?.((prev) =>
            prev
              ? {
                  ...prev,
                  plan: safePlan(prev.plan, credits),
                }
              : prev,
          );

          setEntityData?.((prev) =>
            prev
              ? {
                  ...prev,
                  plan: safePlan(prev.plan, credits),
                }
              : prev,
          );

          setFinalEntity((prev) =>
            prev
              ? {
                  ...prev,
                  plan: safePlan(prev.plan, credits),
                }
              : prev,
          );
        }

        /* -------------------- REDUX -------------------- */
        dispatch(setEntitydata(updatedList));

        /* -------------------- CURRENT USER -------------------- */
        if (currentEntity?.id === currentUser?.id && ownUsed) {
          dispatch(
            setUser({
              ...currentUser,
              plan: safePlan(currentUser?.plan, credits),
            }),
          );
        }

        /* -------------------- AUTO STOP WHEN NO CREDITS -------------------- */
        if (credits < getHostCostPerMin()) {
          const clientError = {
            message: Loc.currentLang.liveStream.hostEnded,
          };
          CustomToast({ clientError });

          await leaveLive();
          setShowCreditsPopup(true);
        }
      } catch (err) {
        console.error("Charge API error", err);
      }
    }, 60000);
  };

  /* ---------- STOP TIMER ---------- */
  const stopChargeTimer = () => {
    if (chargeTimer.current) {
      clearInterval(chargeTimer.current);
      chargeTimer.current = null;
    }
  };

  /* ---------- CLEANUP ---------- */
  useEffect(() => {
    return () => {
      stopChargeTimer();
    };
  }, []);

  /* ===================== INITIAL LOAD ===================== */
  useEffect(() => {
    initPreview();

    return () => {
      stopChargeTimer();
      //leaveLive();
    };
  }, []);

  /* ===================== LOW CREDIT WARNING ===================== */
  useEffect(() => {
    if (
      creditsAvailable <= HOST_START_CREDITS &&
      creditsAvailable > 0 &&
      liveStream
    ) {
      setShowLowCreditPopup(true);
    }
  }, [creditsAvailable]);

  /* ===================== AUTO STOP WHEN NO CREDITS ===================== */
  //useEffect(() => {
  //  const handleStop = async () => {
  //    if (creditsAvailable < HOST_COST_PER_MIN && liveStream) {
  //      const clientError = {
  //        message: Loc.currentLang.liveStream.hostEnded,
  //      };
  //      CustomToast({ clientError });

  //      await leaveLive();
  //      setShowCreditsPopup(true);
  //    }
  //  };

  //  handleStop();
  //}, [creditsAvailable, liveStream]);

  const getHostCostPerMin = () => (isRecording ? 100 : 50);

  /* ===================== CLOSE SESSION ON TAB CLOSE ===================== */
  useEffect(() => {
    return () => {
      if (sessionIdRef.current) {
        SignalRService.leaveLiveChat(sessionIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleUnload = () => {
      dispatch(setPostPopupExpanded(false));
      dispatch(setHostLiveMinimized(false));

      if (sessionIdRef.current) {
        AV.Media.closeLiveSession({
          target: AV.AVEntity.getFk(entity),
          minutes: accumulatedMinutes.current ?? 0,
          mode: "Host",
          channelId: liveStream?.channelId ?? "",
          resourceId: liveStream?.resourceId ?? "",
          sid: liveStream?.sid ?? "",
        });

        SignalRService.leaveLiveChat(sessionIdRef.current);
      }
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  const startTimeRef = useRef<number>(0);
  const [liveAgo, setLiveAgo] = useState("");
  const [liveDuration, setLiveDuration] = useState("00:00:00");

  // ===================== INITIALIZE START TIME =====================
  useEffect(() => {
    if (!liveStream?.date) return;

    const dateObj = new Date(liveStream.date);
    startTimeRef.current = dateObj.getTime();
  }, [liveStream?.date]);

  // ===================== LIVE DURATION TIMER =====================
  useEffect(() => {
    if (!startTimeRef.current) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = now - startTimeRef.current;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const formatted =
        String(hours).padStart(2, "0") +
        ":" +
        String(minutes).padStart(2, "0") +
        ":" +
        String(seconds).padStart(2, "0");

      setLiveDuration(formatted);
    }, 1000);

    return () => clearInterval(interval);
  }, [liveStream?.date]);

  // ===================== LIVE AGO =====================
  useEffect(() => {
    if (!startTimeRef.current) return;

    const updateTime = () => {
      const now = Date.now();
      const diff = now - startTimeRef.current;

      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      let text = "";

      if (minutes < 1) {
        text = Loc.currentLang.convo.justNow;
      } else if (minutes === 1) {
        text = `1 ${Loc.currentLang.convo.minuteAgo}`;
      } else if (minutes < 60) {
        text = `${minutes} ${Loc.currentLang.convo.minutesAgo}`;
      } else if (hours === 1) {
        text = `1 ${Loc.currentLang.convo.hourAgo}`;
      } else if (hours < 24) {
        text = `${hours} ${Loc.currentLang.convo.hoursAgo}`;
      } else if (days === 1) {
        text = `1 ${Loc.currentLang.convo.daysAgo}`;
      } else {
        text = `${days} ${Loc.currentLang.convo.daysAgo}`;
      }

      setLiveAgo(text);
    };

    updateTime(); // initial

    const interval = setInterval(updateTime, 60000); // update every 1 min

    return () => clearInterval(interval);
  }, [liveStream?.date]);

  //#endregion Livestream Section

  //#region Recording(Livestream) Section
  const startRecording = async () => {
    try {
      if (!liveStream) return;

      setIsAPICall(true);
      setIsRecordingLoading(true);
      const result = await AV.Media.startLiveRecording({
        target: liveStream.owner,
        channelId: liveStream.channelId,
        token: liveStream.token,
        webRecording: !isMobile,
      });

      setLiveStream((prev) =>
        prev
          ? {
              ...prev,
              resourceId: result.resourceId,
              sid: result.sid,
            }
          : prev,
      );

      setShowLiveViewer?.((prev) =>
        prev
          ? {
              ...prev,
              resourceId: result.resourceId,
              sid: result.sid,
            }
          : prev,
      );

      setIsRecording(true);
      recordingStartedAfterLiveRef.current = true;
      recordingStartTimeRef.current = Date.now();
    } catch (error) {
      console.error("Start Recording Error:", error);
    } finally {
      setConfirmType("");
      setConfirmText("");
      setIsConfirm(false);
      setIsAPICall(false);
      setIsRecordingLoading(false);
    }
  };

  const stopRecording = async () => {
    try {
      if (!liveStream) return;

      setIsAPICall(true);
      setIsRecordingLoading(true);
      const ml = await AV.Media.stopLiveRecording({
        target: liveStream.owner,
        channelId: liveStream.channelId,
        resourceId: liveStream.resourceId,
        sid: liveStream.sid,
        visibility:
          entity?.type === "Event" ||
          //entity?.type === "Game" ||
          entity?.type === "Channel"
            ? "Private"
            : visibilityMap[viewOption],
      });

      setIsRecording(false);
      recordingStartTimeRef.current = 0;
      setRecordingDuration("00:00:00");
      recordingStartedAfterLiveRef.current = false;

      if (ml) {
        setEntity?.(ml);

        dispatch(
          setEntitydata(
            entityDataList.map((item) => (item.id === ml.id ? ml : item)),
          ),
        );

        await updateFeeds();
      }
    } catch (error) {
      console.error("Stop Recording Error:", error);
    } finally {
      setConfirmType("");
      setConfirmText("");
      setIsConfirm(false);
      setIsAPICall(false);
      setIsRecordingLoading(false);
    }
  };

  const updateFeeds = async () => {
    let owner = liveStream?.owner ?? currentUser;
    let userfeed = userfeeds?.filter((x) => x?.owner?.id == owner?.id);
    let param: AV.IFeedParam = {
      feed: { id: owner?.id, pk: owner?.id, type: "Feed" },
      forward: true,
      startDate: new Date(userfeed?.length > 0 ? userfeed[0]?.lastUpdated : 0),
    };

    if (param?.feed?.id) {
      //let response : any = await AV.Feed.getFeed( param );
      console.log("Get Feed from CreateAlbumPopup.tsx -1");

      const feedParams: AV.IFeedParam[] = [param];
      const feedsResponse: any = (await AV.Feed.getAllFeeds(
        feedParams,
      )) as AV.IConversation[];

      let allPosts: Array<{
        id: string;
        date: string;
        owner: AV.IFK;
        ownerType: string | null;
      }> = [];

      // Combine all followIds, privateIds, and publicIds for each feed
      feedsResponse.forEach((feed) => {
        const feedFK = AV.AVEntity.getFk(feed);
        const currentEntityPostIds = _.uniqBy(
          [
            ...(feed?.followIds || []),
            ...(feed?.privateIds || []),
            ...(feed?.publicIds || []),
            ...(feed?.orgIds || []),
          ],
          "id",
        );

        currentEntityPostIds.forEach((postId) => {
          if (postId.id && postId.date) {
            allPosts.push({
              id: postId.id,
              date: new Date(postId.date ?? new Date()).toISOString(),
              owner: feedFK,
              ownerType: feed.type || null, // Assuming feed has a type property
            });

            if (Array.isArray(postId?.gallery) && postId.gallery.length > 0) {
              postId.gallery.forEach((galleryId) => {
                allPosts.push({
                  id: galleryId,
                  date: new Date(postId.date ?? new Date()).toISOString(),
                  owner: feedFK,
                  ownerType: feed.type || null,
                });
              });
            }
          } else {
            console.warn("Post ID or date is missing:", postId);
          }
        });
      });

      const group: { [key: string]: AV.IPostListParam } = {};
      // Group privateIds by owner/feed ID
      allPosts.forEach(({ id }) => {
        if (!group[owner?.id])
          group[owner?.id] = { feed: param?.feed, ids: [] };
        (group[owner?.id].ids as string[]).push(id);
      });

      const batched: AV.IPostListParam[] = Object.values(group); // Convert to array
      const flattened = batched.flatMap((group) =>
        (group?.ids ?? []).map((id) => ({
          feed: group.feed,
          id,
        })),
      );

      const first20 = flattened.slice(0, 20); // Take only first 20 posts for batching

      // Group again for Feed.getAllPosts format
      const groupByFeed = (items: { feed: any; id: string }[]) => {
        const map = new Map<string, { feed: any; ids: string[] }>();
        items.forEach(({ feed, id }) => {
          if (!map.has(feed.id)) {
            map.set(feed.id, { feed, ids: [] });
          }
          map.get(feed.id)!.ids.push(id);
        });
        return Array.from(map.values());
      };

      const firstBatch = groupByFeed(first20); // Final grouped format for API

      // Fetch posts for first batch
      let fetchPosts = firstBatch.length
        ? await AV.Feed.getAllPosts(firstBatch)
        : [];

      // Fetch replies if nested posts exist
      if (fetchPosts && Array.isArray(fetchPosts)) {
        const updatedPostList = await fetchReplyPosts(fetchPosts, firstBatch);
        fetchPosts = updatedPostList;
      }

      let allFeeds = userfeeds.concat(fetchPosts)
        ? userfeeds.concat(fetchPosts)
        : fetchPosts; // Combine existing and fetched posts
      allFeeds = _.uniqBy(allFeeds, "id"); // Remove duplicate posts
      allFeeds = list_to_tree(allFeeds); // Convert flat list to tree (for threaded posts)

      // Sort posts by createDate descending
      allFeeds = allFeeds.sort((a, b) => {
        const dateA = new Date(a?.createDate).getTime() ?? 0;
        const dateB = new Date(b?.createDate).getTime() ?? 0;
        return dateB - dateA;
      });

      dispatch(setFeeds(allFeeds));
    }
  };

  async function fetchReplyPosts(
    postList: AV.IPost[],
    postsToFetch: AV.IPostListParam[],
  ): Promise<AV.IPost[]> {
    const replyPostIds = postList
      .filter((x) => Array.isArray(x.posts) && x.posts.length > 0) // Only posts with nested replies
      .map((x) => ({ id: x.id, pk: x.pk, posts: x.posts })); // Collect their post IDs and pk

    if (replyPostIds.length === 0) return postList; // No replies to fetch

    const replyPostListParams: AV.IPostListParam[] = [];
    const uniquePks = [
      ...new Set(
        replyPostIds.map((r) => r.pk).filter((pk): pk is string => !!pk),
      ),
    ]; // Unique pk list

    // Match original fetch request with replies using pk
    uniquePks.forEach((pk) => {
      const matchedFeed = postsToFetch.filter((x) => x.feed.pk === pk);
      if (matchedFeed.length > 0) {
        replyPostListParams.push({
          feed: matchedFeed[0].feed,
          ids: replyPostIds.find((x) => x.pk === pk)?.posts || [],
        });
      }
    });

    const replyPostList = await AV.Feed.getAllPosts(replyPostListParams); // Fetch reply posts
    return _.uniqBy([...postList, ...replyPostList], "id"); // Merge with original and dedupe
  }

  const list_to_tree = (list) => {
    var map = {},
      node: any,
      roots: any = [],
      i;

    for (i = 0; i < list?.length; i += 1) {
      map[list[i]?.id] = i; // initialize the map
      if (list[i]?.children?.length === 0 || !list[i]?.children)
        list[i]["children"] = []; // initialize the children
      if (list[i]?.galleryPost?.length === 0 || !list[i]?.galleryPost)
        list[i]["galleryPost"] = [];
    }

    for (i = 0; i < list?.length; i += 1) {
      node = list[i];
      if (node.parentPost !== undefined) {
        if (
          list[map[node.parentPost]]?.gallery?.items?.filter(
            (x) => x === node.id,
          )?.length > 0 &&
          node.gallery == undefined
        ) {
          let excitingpost = list[map[node.parentPost]]?.galleryPost?.filter(
            (x) => x?.id === node?.id,
          );
          if (excitingpost?.length === 0 || excitingpost === undefined)
            list[map[node.parentPost]]?.galleryPost.push(node);
        } else {
          let excitingcomment = list[map[node.parentPost]]?.children?.filter(
            (x) => x?.id === node?.id,
          );
          if (excitingcomment?.length === 0 || excitingcomment === undefined)
            list[map[node.parentPost]]?.children.push(node);
        }
      } else {
        roots.push(node);
      }
    }

    return roots;
  };

  useEffect(() => {
    if (!isRecording) return;

    const interval = setInterval(() => {
      if (!recordingStartTimeRef.current) return;

      const diff = Date.now() - recordingStartTimeRef.current;

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      setRecordingDuration(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [isRecording]);
  //#endregion Recording(Livestream) Section

  //#region SignalR(Live Chat) Section

  /* ===================== STATE ===================== */
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");

  const [joinMessages, setJoinMessages] = useState<
    { id: number; name: string }[]
  >([]);
  const [joinUsers, setJoinUsers] = useState<AV.ILink[]>([]);
  const [showParticipants, setShowParticipants] = useState(false);

  /* ================= REACTIONS ================= */
  const [reactions, setReactions] = useState<any[]>([]);
  const [showMoreReactions, setShowMoreReactions] = useState(false);
  const [reactionCounts, setReactionCounts] = useState({});
  const impressionTypes = useMemo(
    () => [
      { type: "Like", icon: settings.like, alt: "like" },
      { type: "Unlike", icon: settings.unlike, alt: "unLike" },
      { type: "heart", icon: settings.heart, alt: "heart" },
      { type: "Wow", icon: settings.wow, alt: "wow" },
      { type: "Lol", icon: settings.lol, alt: "lol" },
      { type: "Cheer", icon: settings.cheer, alt: "cheer" },
      { type: "Question", icon: settings.question, alt: "Question" },
      { type: "broken", icon: settings.broken, alt: "broken" },
      { type: "Sad", icon: settings.sad, alt: "sad" },
      { type: "Mad", icon: settings.mad, alt: "mad" },
    ],
    [settings],
  );

  const reactionRef = useRef(null);
  useOutsideAlerter(reactionRef, () => setShowMoreReactions(false)); // Close menu when clicking outside

  const participantsRef = useRef(null);
  useOutsideAlerter(participantsRef, () => setShowParticipants(false));

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /* ================= ADD REACTION ================= */
  const addReaction = (emoji: string, user: string) => {
    const id = Date.now();

    const translateY = new Animated.Value(120);
    const translateX = new Animated.Value(0);
    const opacity = new Animated.Value(1);
    const scale = new Animated.Value(0.6);

    // CURVE PATH CONTROL
    const curve = new Animated.Value(0);

    const newReaction = {
      id,
      emoji,
      user,
      translateY,
      translateX,
      opacity,
      scale,
      curve,
      rightOffset: Math.random() * 20 + 5,
    };

    setReactions((prev) => [...prev, newReaction]);

    /* ================= ANIMATION ================= */
    Animated.parallel([
      // FLOAT UP
      Animated.timing(translateY, {
        toValue: -window.innerHeight * 0.8,
        duration: 4000,
        useNativeDriver: true,
      }),

      // CURVE CONTROL (important)
      Animated.timing(curve, {
        toValue: 1,
        duration: 4000,
        useNativeDriver: true,
      }),

      // SCALE POP
      Animated.sequence([
        Animated.timing(scale, {
          toValue: 1.4,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(scale, {
          toValue: 1,
          duration: 3700,
          useNativeDriver: true,
        }),
      ]),

      // FADE
      Animated.sequence([
        Animated.delay(2500),
        Animated.timing(opacity, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: true,
        }),
      ]),
    ]).start(() => {
      setReactions((prev) => prev.filter((r) => r.id !== id));
    });
  };

  /* ===================== SIGNALR HOOK ===================== */
  useLiveSignalR({
    channelId: liveStream?.channelId || showLiveHost?.channelId,
    setMessages,
    setJoinMessages,
    setJoinUsers,
    setReactionCounts,
    addReaction,
    formatText,
    Loc,
    leaveLive,
    userId: currentUser?.id,
    hostId: liveStream?.host?.id,
  });

  /* ===================== SEND MESSAGE ===================== */
  const sendMessage = async () => {
    if (!input.trim()) return;

    const channelId = liveStream?.channelId || showLiveHost?.channelId;
    if (!channelId) return;

    const msg = {
      id: Date.now(),
      text: input,
      user: AV.AVEntity.getLink(currentUser),
    };

    await SignalRService.sendLiveMessage(channelId, msg);

    setInput("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  /* ===================== SEND REACTION ===================== */
  const sendReaction = async (emoji: string) => {
    const channelId = liveStream?.channelId || showLiveHost?.channelId;
    if (!channelId) return;

    await SignalRService.sendLiveReaction(channelId, emoji);
  };

  /* ===================== AUTO SCROLL ===================== */
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  /* ===================== AUTO REMOVE JOIN MSG ===================== */
  useEffect(() => {
    if (!joinMessages.length) return;

    const timer = setTimeout(() => {
      setJoinMessages((prev) => prev.slice(1));
    }, 3000);

    return () => clearTimeout(timer);
  }, [joinMessages, joinUsers]);

  //#endregion SignalR(Live Chat) Section

  useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      const finalEntity = entity;
      const fk = AV.AVEntity.getFk(finalEntity);

      const handleCredits = (credits: number) => {
        setCreditsAvailable(credits);
      };

      // Helper: Load Shared Plan Credits
      const loadSharedPlanCredits = async (entity: AV.IAVEntity) => {
        try {
          let shared =
            entityDataList?.find((x) => x?.id === entity.plan?.shared?.id) ??
            null;

          if (!shared) {
            shared = await AV.AVEntity.getSharedPlan(AV.AVEntity.getFk(entity));
            if (!isMounted) return;

            entityDataList?.push(shared);
            dispatch(setEntitydata(entityDataList));
          }

          setSharedPlan(shared);
          setFinalEntity(entity);
          handleCredits(shared?.plan?.credits ?? 0);
        } catch (e) {
          if (isMounted) console.error("Failed to load shared plan:", e);
        }
      };

      // NOT EVENT, check shared plan directly
      if (finalEntity?.type !== "Event" && finalEntity?.type !== "Game") {
        if (finalEntity.plan?.shared) {
          await loadSharedPlanCredits(finalEntity);
        } else {
          setFinalEntity(finalEntity);
          setCreditsAvailable(finalEntity?.plan?.credits ?? 0);
          handleCredits(finalEntity?.plan?.credits ?? 0);
        }
        return;
      }

      // EVENT, lookup parent entity
      try {
        let parentEntity =
          entityDataList?.find((x) => x?.id === finalEntity?.parent?.id) ??
          null;

        if (!parentEntity && finalEntity?.parent) {
          parentEntity = await AV.AVEntity.get(finalEntity?.parent);
          if (!isMounted) return;

          entityDataList?.push(parentEntity);
          dispatch(setEntitydata(entityDataList));
        }

        // Parent Shared Plan?
        if (parentEntity.plan?.shared) {
          await loadSharedPlanCredits(parentEntity);
        } else {
          setFinalEntity(parentEntity);
          setCreditsAvailable(parentEntity?.plan?.credits ?? 0);
          handleCredits(parentEntity?.plan?.credits ?? 0);
        }
        setEventParentEntity(parentEntity);
      } catch (error) {
        if (isMounted) console.error("Failed to load event credits:", error);
      }
    };

    if (!liveStream) loadPlan();

    return () => {
      isMounted = false;
    };
  }, [owner, currentUser, entity, showCreditsPopup]);

  useEffect(() => {
    dispatch(setPostPopupExpanded(true));

    return () => {
      dispatch(setPostPopupExpanded(false));
      //dispatch(setHostLiveMinimized(false));
    };
  }, [dispatch]);

  useLiveSignalR({
    channelId: liveStream?.channelId || showLiveHost?.channelId,
    setMessages,
    setJoinMessages,
    setJoinUsers,
    setReactionCounts,
    addReaction,
    formatText,
    Loc,
  });

  const recordAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    let animation: Animated.CompositeAnimation;

    if (isRecording) {
      animation = Animated.loop(
        Animated.sequence([
          Animated.timing(recordAnim, {
            toValue: 0,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(recordAnim, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
        ]),
      );

      animation.start();
    } else {
      recordAnim.stopAnimation();
      recordAnim.setValue(1);
    }

    return () => animation?.stop();
  }, [isRecording]);

  /* -------------------- UI -------------------- */

  const AVPreLivePopup = ({
    entity,
    isAPICall,
    startLive,
    videoUsers,
    dropdownRef,
    toggleMic,
    toggleVideo,
    isMicMuted,
    isVideoMuted,
    viewOption,
    setViewOption,
    openDropdown,
    setOpenDropdown,
    creditsAvailable,
    setShowCreditsPopup,
  }) => {
    const user = videoUsers?.[0];

    return (
      <Box
        position="relative"
        w="95%"
        maxW="720px"
        maxH="90vh"
        borderRadius="8px"
        bg="white"
        p={{ base: "12px", md: "16px" }}
        shadow={8}
        alignSelf="center"
      >
        {/* ================= VIDEO SECTION ================= */}
        <Box
          position="relative"
          h={{ base: "260px", md: "40vh" }}
          minH="220px"
          maxH="450px"
          borderRadius="8px"
          overflow="hidden"
        >
          {/* CLOSE BUTTON */}
          <Pressable
            position="absolute"
            top="10px"
            right="10px"
            zIndex={100}
            w="36px"
            h="36px"
            bg="#FF3B30"
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            onPress={leaveLive}
          >
            <AVCancel
              callback={leaveLive}
              color="white"
              size="24px"
              hoverColor="none"
            />
          </Pressable>

          {/* VIDEO */}
          {user && (
            <Box
              ref={(el) => {
                if (el && user.videoTrack) {
                  user.videoTrack.play(el);
                }
              }}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
            />
          )}

          {/* OVERLAY */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0,0,0,0.3)"
          />

          {/* CENTER CONTENT */}
          <VStack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            alignItems="center"
            justifyContent="center"
            space={3}
          >
            <Box alignItems="center" justifyContent="center">
              <PreLivestreamIcon height="64px" width="64px" />
            </Box>

            <Text fontSize="16px" fontWeight="600" color="white">
              {Loc.currentLang.liveStream.readyToGoLive}
            </Text>

            <HStack space={3}>
              <Pressable
                onPress={toggleMic}
                w="40px"
                h="40px"
                borderRadius="full"
                bg={isMicMuted ? "red.500" : "#32AE78"}
                alignItems="center"
                justifyContent="center"
              >
                {isMicMuted ? (
                  <FiMicOff size={16} color="white" />
                ) : (
                  <FiMic size={16} color="white" />
                )}
              </Pressable>

              <Pressable
                onPress={toggleVideo}
                w="40px"
                h="40px"
                borderRadius="full"
                bg={isVideoMuted ? "red.500" : "#32AE78"}
                alignItems="center"
                justifyContent="center"
              >
                {isVideoMuted ? (
                  <FiVideoOff size={16} color="white" />
                ) : (
                  <FiVideo size={16} color="white" />
                )}
              </Pressable>
            </HStack>
          </VStack>
        </Box>

        {/* ================= CONTENT ================= */}
        <div
          ref={scrollContainerRef}
          style={{
            marginTop: "10px",
            flex: 1,
            overflow: "auto",
            maxHeight: "350px",
          }}
          onScroll={(e) => {
            scrollPositionRef.current = e.currentTarget.scrollTop;
          }}
        >
          <VStack space={4} alignItems="center">
            {/* RECORD MEETING */}
            <VStack mt={4} mb={4}>
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontSize="24px" fontWeight="600" color="#333">
                  {Loc.currentLang.liveStream.recordMeeting}
                </Text>

                <Switch
                  isChecked={isRecording}
                  onToggle={() => setIsRecording(!isRecording)}
                  size="lg"
                  onTrackColor="#32AE78"
                />
              </HStack>

              <Text mt={2} fontSize="14px" color="#777" lineHeight="22px">
                {Loc.currentLang.liveStream.recordMeetingDescription}
              </Text>
            </VStack>

            {/* CREDITS */}
            <Text fontSize="16px" fontWeight="bold">
              {Loc.currentLang.myPlan.availableCredits}
            </Text>

            <DollarCoinsIcon height="80px" width="80px" />

            <Text fontSize="2xl" fontWeight="bold">
              {creditsAvailable}
            </Text>

            <Pressable onPress={() => setShowCreditsPopup(true)}>
              <Text fontSize="sm" color="#00A7BC" underline>
                {Loc.currentLang.manageConversation.buyMoreCredits}
              </Text>
            </Pressable>

            {/* INFO */}
            <HStack alignItems="center" space={2} px="10px">
              <Image source={inform} alt="info" size="16px" />

              {(() => {
                const minutes = Math.max(
                  1,
                  Math.floor(creditsAvailable / getHostCostPerMin()),
                );

                const message = formatText(
                  Loc.currentLang.liveStream.hostCreditsInfo,
                  [creditsAvailable, minutes, minutes !== 1 ? "s" : ""],
                );

                return (
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {message}
                  </Text>
                );
              })()}
            </HStack>

            {/* DROPDOWN */}
            {entity?.type !== "Event" &&
              //entity?.type !== "Game" &&
              entity?.type !== "Channel" && (
                <HStack space={3} alignItems="center" flexWrap="wrap">
                  <Text fontSize="16px">
                    {Loc.currentLang.liveStream.audience} :
                  </Text>

                  <Box position="relative" ref={dropdownRef}>
                    <Button
                      w={{ base: "140px", md: "180px" }}
                      bg="rgba(0,166,188,0.15)"
                      _hover={{ bg: "rgba(0,166,188,0.15)" }}
                      _pressed={{ bg: "rgba(0,166,188,0.15)" }}
                      rounded="full"
                      justifyContent="space-between"
                      px="16px"
                      onPress={() => setOpenDropdown(!openDropdown)}
                    >
                      <Text color="rgba(0,167,188,1)" fontSize="14px">
                        {viewOption}
                      </Text>
                    </Button>

                    <Pressable
                      position="absolute"
                      right="12px"
                      top="16px"
                      onPress={() => setOpenDropdown(!openDropdown)}
                    >
                      <DropdownIcon height="9" width="16" />
                    </Pressable>

                    {openDropdown && (
                      <Box
                        position="absolute"
                        bottom="110%"
                        w="100%"
                        bg="white"
                        borderRadius="10px"
                        shadow={6}
                        zIndex={9999}
                      >
                        {[
                          Loc.currentLang.menu.followers,
                          Loc.currentLang.menu.private,
                        ].map((item) => (
                          <Pressable
                            key={item}
                            px="12px"
                            py="10px"
                            onPress={() => {
                              setViewOption(item);
                              setOpenDropdown(false);
                            }}
                            bg={viewOption === item ? "#F5F5F5" : "white"}
                          >
                            <Text>{item}</Text>
                          </Pressable>
                        ))}
                      </Box>
                    )}
                  </Box>
                </HStack>
              )}

            {/* GO LIVE BUTTON */}
            <Button
              w={{ base: "70%", md: "30%" }}
              rounded="full"
              bg="AVColor.primary"
              _hover={{ bg: "AVColor.primary" }}
              _pressed={{ bg: "AVColor.primary" }}
              isLoading={isAPICall}
              isDisabled={isAPICall}
              onPress={startLive}
            >
              {Loc.currentLang.liveStream.goLive}
            </Button>
          </VStack>
        </div>
      </Box>
    );
  };

  return (
    <>
      {open && (
        <Box
          position={isMinimized ? "relative" : "fixed"}
          top={isMinimized ? undefined : 0}
          left={isMinimized ? undefined : 0}
          right={isMinimized ? undefined : 0}
          bottom={isMinimized ? undefined : 0}
          bg={isMinimized ? "transparent" : "rgba(0,0,0,0.4)"}
          alignItems="center"
          justifyContent="center"
          zIndex={999}
          px={isMinimized ? "0px" : "12px"}
          w="100%"
          h="100%"
        >
          {/* ================= PRE LIVE ================= */}
          {!isLive ? (
            <AVPreLivePopup
              entity={entity}
              isAPICall={isAPICall}
              startLive={startLive}
              videoUsers={videoUsers}
              dropdownRef={dropdownRef}
              toggleMic={toggleMic}
              toggleVideo={toggleVideo}
              isMicMuted={isMicMuted}
              isVideoMuted={isVideoMuted}
              viewOption={viewOption}
              setViewOption={setViewOption}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              creditsAvailable={creditsAvailable}
              setShowCreditsPopup={setShowCreditsPopup}
            />
          ) : (
            <Box
              position={isMinimized ? "relative" : "relative"}
              width={isMinimized ? "100%" : "80vw"}
              height={isMinimized ? "100%" : "95vh"}
              borderRadius="12px"
              overflow="hidden"
              bg="black"
              zIndex={99999}
            >
              {/* ================= VIDEO ================= */}
              {videoUsers.map((user) => (
                <Box
                  key={user.uid}
                  ref={(el) => {
                    if (el && user.videoTrack) {
                      user.videoTrack.play(el, { fit: "cover" });
                    }
                  }}
                  position="absolute"
                  top={0}
                  left={0}
                  w="100%"
                  h="100%"
                />
              ))}

              {!showOnlyVideo && !isMinimized && (
                <>
                  {/* ================= TOP LEFT ================= */}
                  <HStack
                    position="absolute"
                    top="16px"
                    left="16px"
                    alignItems="center"
                    space={2}
                  >
                    {/* PROFILE + NAME BLOCK */}
                    <HStack
                      alignItems="center"
                      space={2}
                      bg="#00000026"
                      px="10px"
                      py="6px"
                      borderRadius="full"
                    >
                      <EntityProfilePic
                        height={10}
                        width={10}
                        link={AV.AVEntity.getLink(host ?? currentUser)}
                      />

                      <VStack space={0}>
                        <Text color="white" fontSize="14px" fontWeight="bold">
                          {host?.name ?? currentUser?.name}
                        </Text>
                        <Text color="white" fontSize="12px" opacity={0.7}>
                          {liveAgo}
                        </Text>
                      </VStack>
                    </HStack>

                    {/* VIEW COUNT PILL */}
                    <Pressable
                      onPress={() => setShowParticipants((prev) => !prev)}
                    >
                      <HStack
                        alignItems="center"
                        bg="#353130"
                        px="6px"
                        py="4px"
                        borderRadius="full"
                        space={2}
                      >
                        <FiUsers size={14} color="#FFFFFF" />

                        <Text color="white" fontSize="14px" fontWeight="bold">
                          {joinUsers?.length || 1}
                        </Text>
                      </HStack>
                    </Pressable>

                    {/* PARTICIPANTS POPUP */}
                    {showParticipants && (
                      <Box
                        ref={participantsRef}
                        position="absolute"
                        top="60px"
                        left="20px"
                        bg="white"
                        borderRadius="12px"
                        shadow={8}
                        width="280px"
                        maxH="350px"
                        zIndex={99999}
                        overflow="hidden"
                      >
                        <ScrollView
                          maxH="300px"
                          showsVerticalScrollIndicator={false}
                        >
                          {joinUsers?.length > 0 ? (
                            joinUsers.map((item, index) => (
                              <HStack
                                key={item?.id || index}
                                alignItems="center"
                                space={3}
                                px="12px"
                                py="10px"
                                borderBottomWidth={
                                  index !== joinUsers.length - 1 ? 1 : 0
                                }
                                borderBottomColor="#F1F1F1"
                              >
                                <EntityProfilePic
                                  width={8}
                                  height={8}
                                  link={item}
                                />

                                <VStack flex={1}>
                                  <Text fontSize="14px" fontWeight="600">
                                    {item?.title}{" "}
                                    {currentUser?.id === item?.id && (
                                      <>
                                        ({Loc.currentLang.liveStream.host}){" "}
                                        {Loc.currentLang.menu.you}
                                      </>
                                    )}
                                  </Text>
                                </VStack>
                              </HStack>
                            ))
                          ) : (
                            <Text p="15px" textAlign="center" color="gray.500">
                              {Loc.currentLang.liveStream.noParticipants}
                            </Text>
                          )}
                        </ScrollView>
                      </Box>
                    )}
                  </HStack>

                  {/* ================= TOP CENTER ================= */}
                  <Box
                    position="absolute"
                    top="35px"
                    alignSelf="center"
                    bg="#00000026"
                    px="12px"
                    py="6px"
                    borderRadius="full"
                  >
                    <HStack alignItems="center" space={2}>
                      <HStack
                        alignItems="center"
                        space={1}
                        bg="#F44336"
                        px="10px"
                        py="4px"
                        borderRadius="full"
                      >
                        <LiveIcon width="15px" height="15px" />
                        <Text color="white" fontSize="14px">
                          {Loc.currentLang.liveStream.live}
                        </Text>
                      </HStack>

                      <Box w="1.5px" h="28px" bg="rgba(255,255,255,0.3)" />

                      <Text color="white">{liveDuration}</Text>

                      {isRecording && (
                        <>
                          <Box w="1.5px" h="28px" bg="rgba(255,255,255,0.3)" />

                          <HStack alignItems="center" space={1}>
                            <Animated.View style={{ opacity: recordAnim }}>
                              <LiveRecordIcon width="18px" height="18px" />
                            </Animated.View>

                            <Text color="white">REC {recordingDuration}</Text>
                          </HStack>
                        </>
                      )}
                    </HStack>
                  </Box>

                  {/* CENTER JOIN/LEFT MESSAGES */}
                  <Box
                    position="absolute"
                    top="100px"
                    left={0}
                    right={0}
                    alignItems="center"
                  >
                    {joinMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className="fadeSlide"
                        style={{ marginBottom: "6px" }}
                      >
                        <Box
                          bg="#00000026"
                          px="14px"
                          py="6px"
                          borderRadius="20px"
                        >
                          <Text color="white" fontSize="13px">
                            <Text bold>{msg.name}</Text>
                          </Text>
                        </Box>
                      </div>
                    ))}
                  </Box>
                </>
              )}

              {/* ================= TOP RIGHT(CLOSE & REACTIONS) ================= */}
              {!isMinimized && (
                <Pressable
                  position="absolute"
                  top="30px"
                  right="120px"
                  zIndex={100}
                  w="36px"
                  h="36px"
                  bg="#32AE78"
                  borderRadius="full"
                  alignItems="center"
                  justifyContent="center"
                  onPress={() => {
                    dispatch(setHostLiveMinimized(true));
                    dispatch(setPostPopupExpanded(false));
                  }}
                >
                  <LiveMinIcon />
                </Pressable>
              )}

              {isMinimized && (
                <Pressable
                  position="absolute"
                  top={isMinimized ? "12px" : "30px"}
                  left="20px"
                  zIndex={100}
                  w="36px"
                  h="36px"
                  bg="#32AE78"
                  borderRadius="full"
                  alignItems="center"
                  justifyContent="center"
                  onPress={() => {
                    dispatch(setHostLiveMinimized(false));
                    dispatch(setPostPopupExpanded(true));
                  }}
                >
                  <LiveMaxIcon />
                </Pressable>
              )}

              <Pressable
                position="absolute"
                top={isMinimized ? "12px" : "30px"}
                right="20px"
                zIndex={100}
                w="36px"
                h="36px"
                bg="#FF3B30"
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                onPress={handleLeaveLiveConfirmation}
              >
                <AVCancel
                  callback={handleLeaveLiveConfirmation}
                  color="white"
                  size="24px"
                  hoverColor="none"
                />
              </Pressable>

              {!isMinimized && (
                <Pressable
                  position="absolute"
                  top="30px"
                  right="70px"
                  zIndex={100}
                  w="36px"
                  h="36px"
                  bg="#32AE78"
                  borderRadius="full"
                  alignItems="center"
                  justifyContent="center"
                  onPress={() => setShowOnlyVideo((prev) => !prev)}
                >
                  {showOnlyVideo ? (
                    <FiEyeOff size={16} color="white" />
                  ) : (
                    <FiEye size={16} color="white" />
                  )}
                </Pressable>
              )}

              {!showOnlyVideo && !isMinimized && (
                <>
                  {Object.entries(reactionCounts).length > 0 && (
                    <Box position="absolute" top="100px" right="20px">
                      <HStack
                        bg="#00000026"
                        px="16px"
                        py="8px"
                        borderRadius="full"
                        space={4}
                      >
                        {Object.entries(reactionCounts).map(
                          ([emoji, count]) => {
                            const reaction = impressionTypes.find(
                              (type) => type.type === emoji,
                            );

                            return (
                              <HStack key={emoji} alignItems="center" space={1}>
                                <Image
                                  source={
                                    reaction?.icon
                                      ? { uri: reaction.icon }
                                      : undefined
                                  }
                                  alt={reaction?.alt}
                                  size="18px"
                                />

                                <Text
                                  color="white"
                                  fontSize="14px"
                                  fontWeight="500"
                                >
                                  {count}
                                </Text>
                              </HStack>
                            );
                          },
                        )}
                      </HStack>
                    </Box>
                  )}

                  {/* ================= CHAT CONTAINER ================= */}
                  <div
                    ref={chatRef}
                    style={{
                      position: "absolute",
                      top: 80,
                      bottom: 75,
                      left: 12,
                      width: "80%",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      paddingRight: 4,
                      scrollbarWidth: "none",
                    }}
                  >
                    {/* PUSH CONTENT TO BOTTOM */}
                    <div style={{ flex: 1 }} />

                    <VStack space={4}>
                      {messages.map((msg) => {
                        const isMe = currentUser?.id === msg?.user?.id;

                        const userEntity = isMe
                          ? currentUser
                          : entityDataList?.find(
                              (x) => x?.id === msg?.user?.id,
                            ) || msg?.user;

                        const userLink = AV.AVEntity.getLink(userEntity);

                        return (
                          <HStack
                            key={msg.id}
                            space={2}
                            alignItems="flex-start"
                          >
                            <EntityProfilePic
                              width={10}
                              height={10}
                              link={userLink}
                            />

                            <VStack maxW="45%">
                              <Text
                                fontSize="14px"
                                fontWeight="600"
                                color="#000000D9"
                              >
                                {msg.user.title}
                              </Text>

                              <div
                                style={{
                                  backdropFilter: "blur(10px)",
                                  borderRadius: "14px",
                                  marginTop: "4px",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                <Box
                                  px="14px"
                                  py="10px"
                                  borderRadius="14px"
                                  bg="#98929299"
                                >
                                  <Text color="white" fontSize="14px">
                                    {msg.text}
                                  </Text>
                                </Box>
                              </div>
                            </VStack>
                          </HStack>
                        );
                      })}
                    </VStack>
                  </div>

                  {/* ================= RIGHT CONTROLS ================= */}
                  <HStack
                    position="absolute"
                    right="16px"
                    bottom="90px"
                    space={3}
                  >
                    <Pressable
                      onPress={toggleMic}
                      bg={!isMicMuted ? "#32AE78" : "red.500"}
                      p="10px"
                      borderRadius="full"
                    >
                      {isMicMuted ? (
                        <FiMicOff size={16} color="white" />
                      ) : (
                        <FiMic size={16} color="white" />
                      )}
                    </Pressable>

                    <Pressable
                      onPress={toggleVideo}
                      bg={!isVideoMuted ? "#32AE78" : "red.500"}
                      p="10px"
                      borderRadius="full"
                    >
                      {isVideoMuted ? (
                        <FiVideoOff size={16} color="white" />
                      ) : (
                        <FiVideo size={16} color="white" />
                      )}
                    </Pressable>

                    <Pressable
                      onPress={() => {
                        setConfirmType(
                          isRecording ? "StopRecording" : "StartRecording",
                        );
                        setConfirmText(
                          isRecording
                            ? Loc.currentLang.liveStream.stopRecordDesc
                            : Loc.currentLang.liveStream.startRecordDesc,
                        );
                        setIsConfirm(true);
                      }}
                      bg="red.500"
                      p="10px"
                      borderRadius="full"
                      isDisabled={isRecordingLoading}
                      opacity={isRecordingLoading ? 0.5 : 1}
                    >
                      {isRecordingLoading ? (
                        <Spinner size="sm" color="white" />
                      ) : isRecording ? (
                        <LiveRecordStopIcon width="18px" height="18px" />
                      ) : (
                        <LiveRecordStartIcon width="18px" height="18px" />
                      )}
                    </Pressable>
                  </HStack>

                  {/* ================= FLOATING REACTIONS ================= */}
                  <Box
                    position="absolute"
                    bottom="70px"
                    right="70px"
                    w="160px"
                    h="80%"
                    pointerEvents="none"
                    overflow="hidden"
                    zIndex={60}
                  >
                    {reactions.map((r) => {
                      const reaction = impressionTypes.find(
                        (type) => type.type === r.emoji,
                      );

                      return (
                        <Animated.View
                          key={r.id}
                          style={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            transform: [
                              { translateY: r.translateY },
                              {
                                translateX: r.curve.interpolate({
                                  inputRange: [0, 0.5, 1],
                                  outputRange: [0, -30, 30],
                                }),
                              },
                              { scale: r.scale },
                            ],
                            opacity: r.opacity,
                            alignItems: "center",
                          }}
                        >
                          {reaction && (
                            <Image
                              source={
                                typeof reaction.icon === "string"
                                  ? { uri: reaction.icon }
                                  : reaction.icon
                              }
                              alt={reaction.alt}
                              size="24px"
                            />
                          )}

                          {/* USER NAME */}
                          {/*<Box*/}
                          {/*  mt="2px"*/}
                          {/*  px="6px"*/}
                          {/*  py="2px"*/}
                          {/*  borderRadius="6px"*/}
                          {/*  bg="rgba(0,0,0,0.6)"*/}
                          {/*>*/}
                          {/*  <Text color="white" fontSize="10px">*/}
                          {/*    {r.user}*/}
                          {/*  </Text>*/}
                          {/*</Box>*/}
                        </Animated.View>
                      );
                    })}
                  </Box>

                  {/* ================= BOTTOM BAR ================= */}
                  <Box
                    position="absolute"
                    bottom={0}
                    w="100%"
                    bg="#FFFFFF"
                    px="12px"
                    py="14px"
                  >
                    <HStack alignItems="center" space={2}>
                      {/* REACTIONS */}
                      <HStack space={2}>
                        {[
                          { type: "Like", icon: settings.like, alt: "like" },
                          { type: "heart", icon: settings.heart, alt: "heart" },
                          { type: "Cheer", icon: settings.cheer, alt: "cheer" },
                        ].map((reaction) => (
                          <Pressable
                            key={reaction.type}
                            onPress={() => {
                              sendReaction(reaction.type);
                              setShowMoreReactions(false);
                            }}
                          >
                            <HStack>
                              <Tooltip
                                title={
                                  Loc.currentLang.impression[
                                    reaction.type.toLowerCase()
                                  ]
                                }
                                enterDelay={100}
                                leaveDelay={0}
                                arrow
                                placement="top"
                              >
                                <span style={{ display: "inline-block" }}>
                                  {" "}
                                  <Image
                                    source={{ uri: reaction.icon }}
                                    alt={reaction.alt}
                                    size="26px"
                                  />
                                </span>
                              </Tooltip>
                            </HStack>
                          </Pressable>
                        ))}

                        <Button
                          width="36px"
                          height="36px"
                          bg="#EEEEEEEE"
                          _hover={{ bgColor: "gray.50" }}
                          rounded="full"
                          shadow={1}
                          p={0}
                          onPress={() =>
                            setShowMoreReactions(!showMoreReactions)
                          }
                        >
                          <AddIcon size="20px" color="#000000CC" />
                        </Button>
                      </HStack>

                      {/* INPUT */}
                      <Input
                        ref={inputRef}
                        flex={1}
                        h="44px"
                        bg="white"
                        borderRadius="full"
                        px="16px"
                        fontSize="15px"
                        placeholder={
                          Loc.currentLang.placeholderText.messagePlaceholder
                        }
                        borderWidth={1}
                        borderColor="#D9D9D9"
                        _focus={{
                          bg: "white",
                          borderWidth: 1,
                        }}
                        _hover={{
                          bg: "white",
                          borderWidth: 1,
                        }}
                        value={input}
                        onChangeText={(text) => setInput(text)}
                        onSubmitEditing={() => {
                          if (input.trim()) {
                            sendMessage();
                            setTimeout(() => inputRef.current?.focus(), 0);
                          }
                        }}
                      />

                      {/* SEND */}
                      <Pressable
                        w="40px"
                        h="40px"
                        bg="#32AE78"
                        borderRadius="full"
                        alignItems="center"
                        justifyContent="center"
                        onPress={sendMessage}
                      >
                        <PaperPlaneIcon size="18px" color="#fff" />
                      </Pressable>
                    </HStack>

                    {/* MORE REACTIONS */}
                    {showMoreReactions && (
                      <HStack
                        ref={reactionRef}
                        position="absolute"
                        bottom="80px"
                        left="12px"
                        bg="#FFFFFF"
                        px="12px"
                        py="8px"
                        borderRadius="24px"
                        shadow={4}
                      >
                        {impressionTypes.map((reaction) => (
                          <Pressable
                            key={reaction.type}
                            onPress={() => {
                              sendReaction(reaction.type);
                              setShowMoreReactions(false);
                            }}
                          >
                            <HStack>
                              <Tooltip
                                title={
                                  Loc.currentLang.impression[
                                    reaction.type.toLowerCase()
                                  ]
                                }
                                enterDelay={100}
                                leaveDelay={0}
                                arrow
                                placement="top"
                              >
                                <span style={{ display: "inline-block" }}>
                                  {" "}
                                  <Image
                                    source={{ uri: reaction.icon }}
                                    alt={reaction.alt}
                                    size="26px"
                                  />
                                </span>
                              </Tooltip>
                            </HStack>
                          </Pressable>
                        ))}
                      </HStack>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {showMoreCreditPopup && (
        <Box {...popupOverlay}>
          <Box {...popupBox}>
            <Box position="absolute" top="10px" right="10px">
              <AVCancel
                callback={() => setShowMoreCreditPopup(false)}
                color={undefined}
                size={undefined}
              />
            </Box>

            <Text fontSize="18px" fontWeight="600" mb={2}>
              {Loc.currentLang.liveStream.notEnoughCredits}
            </Text>

            <Text fontSize="14px" color="rgba(0,0,0,0.7)" mb={5}>
              {Loc.currentLang.liveStream.hostMinCredits}
            </Text>

            <HStack justifyContent="flex-end">
              <Button
                bg="AVColor.primary"
                borderRadius="full"
                px={5}
                _hover={{ bg: "AVColor.primary" }}
                _pressed={{ bg: "AVColor.primary" }}
                onPress={() => {
                  setShowMoreCreditPopup(false);
                  setShowCreditsPopup(true);
                }}
              >
                <Text color="white">
                  {Loc.currentLang.manageConversation.buyMoreCredits}
                </Text>
              </Button>
            </HStack>
          </Box>
        </Box>
      )}

      {showLowCreditPopup &&
        (() => {
          const minutes = Math.max(
            1,
            Math.floor(creditsAvailable / getHostCostPerMin()),
          );

          const message = formatText(
            Loc.currentLang.liveStream.hostLowCredits,
            [minutes, minutes !== 1 ? "s" : ""],
          );

          return (
            <Box {...popupOverlay}>
              <Box {...popupBox}>
                <Text fontSize="18px" fontWeight="600" mb={2}>
                  {Loc.currentLang.manageConversation.lowCredits}
                </Text>

                <Text fontSize="14px" color="rgba(0,0,0,0.7)" mb={5}>
                  {message}
                </Text>

                <HStack justifyContent="flex-end" space={3}>
                  <Button
                    variant="ghost"
                    onPress={() => setShowLowCreditPopup(false)}
                  >
                    <Text>{Loc.currentLang.liveStream.continue}</Text>
                  </Button>

                  <Button
                    bg="AVColor.primary"
                    borderRadius="full"
                    px={5}
                    _hover={{ bg: "AVColor.primary" }}
                    _pressed={{ bg: "AVColor.primary" }}
                    onPress={() => {
                      setShowLowCreditPopup(false);
                      setShowCreditsPopup(true);
                    }}
                  >
                    <Text color="white">
                      {Loc.currentLang.manageConversation.buyMoreCredits}
                    </Text>
                  </Button>
                </HStack>
              </Box>
            </Box>
          );
        })()}

      {showCreditsPopup && (
        <Box
          position="fixed"
          bgColor="rgba(0, 0, 0, 0.25)"
          bottom={0}
          right={0}
          top="40px"
          left={0}
          zIndex={99999}
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflowY="auto"
          px="16px"
        >
          <PurchaseCreditsPopup
            entity={
              entity?.type === "Event" || entity?.type === "Game"
                ? eventParentEntity
                : entity
            }
            setEntity={setEntity ?? setEntityData}
            setEntityData={setEntityData}
            sharedPlan={sharedPlan}
            setSharedPlan={setSharedPlan}
            showCreditsPopup={showCreditsPopup}
            setShowCreditsPopup={setShowCreditsPopup}
          />
        </Box>
      )}

      {isConfirm && (
        <AVConfirmationModal
          showconfirmationPopup={isConfirm}
          setshowconfirmationPopup={setIsConfirm}
          Confirmationtext={confirmText}
          type={confirmType}
          item={undefined}
          function={
            confirmType === "StartRecording"
              ? startRecording
              : confirmType === "StopRecording"
                ? stopRecording
                : leaveLive
          }
          isLoader={isAPICall}
        />
      )}
    </>
  );
};

export default AVLiveHostPopup;
